/**
 * Create a generator AND its Client-tab follow-ups as ONE background job.
 *
 * WHY THIS EXISTS. `CreateEquipmentModal` used to await three callables in a row
 * with the form held open behind a spinner:
 *
 *   1. `createGenerator`                     — AppSheet mints the Generator ID (7-11s warm,
 *                                              ~20s+ on a cold start)
 *   2. `fieldService_attachGeneratorContact` — needs that ID
 *   3. `updateGenerator` (`qboLinks`)        — needs the Firestore doc id
 *
 * Users waited ~30s on "Create Equipment". Nothing about steps 2-3 needs the form
 * on screen, so the modal now closes on submit and this hook runs the sequence,
 * with a "Creating…" placeholder in both generator lists and ONE toast that follows
 * the job to done — the same shape as `useCreateRecurringSchedules` (#5179) and
 * `useCreateServiceTicket`.
 *
 * WHY ONE mutationFn AND NOT THREE HOOKS. Follow-up `mutateAsync`s and per-call
 * callbacks belong to the component that started them, and React Query v5 drops a
 * per-call callback as soon as the same observer mutates again. Owning the whole
 * sequence here — and reporting failure through the HOOK-level `onCreateFailed`,
 * which fires for every mutation — means the job finishes and its failure is
 * reported regardless of what the UI does next.
 *
 * FAILURE SEMANTICS:
 *   - Step 1 failing fails the job. Whether a retry is safe depends on HOW it failed —
 *     see `isDefinitiveCreateFailure`.
 *   - Steps 2-3 failing do NOT fail it. The unit is already real in AppSheet and cannot
 *     be un-created, so the honest outcome is "unit saved, this part did not", with the
 *     place to finish it. They come back as `warnings` and are toasted separately.
 *
 * @module hooks/equipment/useCreateEquipment
 */
import { useIsMutating, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { createElement } from 'react';
import { GeneratorCreatedToast } from '../../components/Equipment/GeneratorCreatedToast';
import { functions } from '../../config/firebase';
import { isNeedsSignalError, withNeedsSignal } from '../../lib/needsSignalMutation';
import { UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS } from '../../config/generatorCallables';
import type { Equipment } from '../../types/equipment';
import { useWarnBeforeUnload } from '../core/useWarnBeforeUnload';
import { dataHubContactKeys } from '../useDataHubContacts';
import { dataHubEquipmentKeys } from '../useDataHubEquipment';
import { generatorKeys } from '../useGenerator';
import { generatorListKeys, type Generator } from '../useGenerators';
import {
  messageFor as contactMessageFor,
  type AttachGeneratorContactInput,
  type AttachGeneratorContactResult,
} from '../fieldService/useAttachGeneratorContact';
import { sharedContactKeys } from '../fieldService/useSharedContactMutations';
import {
  buildOptimisticEquipment,
  newPendingGeneratorId,
  type CreateGeneratorInput,
  type UpdateGeneratorInput,
} from './useGeneratorMutations';

/** Shared by the mutation and `useIsMutating`, so overlapping creates can see each other. */
export const CREATE_EQUIPMENT_MUTATION_KEY = ['equipment', 'create'] as const;

/** `fieldService_attachGeneratorContact`'s server budget — see useAttachGeneratorContact. */
const ATTACH_CONTACT_TIMEOUT_MS = 180_000;
/**
 * How long the client waits for `createGenerator`: past its server `timeoutSeconds: 120`
 * (functions/src/projects/generatorApi.ts), so the client never gives up on a create the
 * server is still finishing — an abandoned wait reports a failure for a unit that may exist.
 */
export const CREATE_GENERATOR_CALLABLE_TIMEOUT_MS = 125_000;

/** Every Field Service generators list, whatever its orgId segment. */
const GENERATOR_LISTS_KEY = [...generatorListKeys.all, 'list'] as const;

type QboLink = NonNullable<UpdateGeneratorInput['updates']['qboLinks']>[number];

export interface CreateEquipmentInput {
  generator: CreateGeneratorInput;
  /** Client-tab contact, attached once the Generator ID exists. */
  contact?: Omit<AttachGeneratorContactInput, 'generatorId'>;
  /**
   * Client-tab QuickBooks customer. Sent as a ONE-element `qboLinks` array, which the
   * server applies as a wholesale replace — correct only because the unit is brand new.
   * Never reuse this for an existing unit (CLAUDE.md recurring defect shape #1).
   */
  qboLink?: QboLink;
}

export interface CreateGeneratorResult {
  id: string;
  generatorId?: string;
  replayed?: boolean;
}

export interface CreateEquipmentOutput {
  generator: CreateGeneratorResult;
  contact?: AttachGeneratorContactResult;
  /** Follow-ups that did not complete. The unit itself exists. */
  warnings: string[];
}

interface CreateEquipmentContext {
  toastId: string;
  /** The placeholder id this job inserted — the only row this job ever removes. */
  pendingId: string;
}

export interface UseCreateEquipmentOptions {
  /**
   * Called as soon as AppSheet has minted the Generator ID — BEFORE the contact and
   * QuickBooks follow-ups run. For a caller that only needs the ID (the ticket form's
   * quick-create), this is when the unit becomes usable; the follow-ups carry on.
   */
  onGeneratorCreated?: (input: CreateEquipmentInput, generator: CreateGeneratorResult) => void;
  /**
   * The create reported success WITHOUT a Generator ID. The unit very likely exists, so
   * this is neither a success a caller can use nor a failure it may retry — a caller
   * waiting on `onGeneratorCreated` must hear about it or it waits forever.
   */
  onGeneratorIdMissing?: (input: CreateEquipmentInput) => void;
  /**
   * How the error toast tells the user to get their entries back. Defaults to the
   * Create Equipment modal's wording; another entry point must name its own.
   */
  restoreHint?: string;
  /**
   * Called for EVERY failed create, including ones started before the caller's latest
   * `mutate` (a per-call `onError` would be dropped for those). `definitive` says
   * whether the server released its idempotency lock — see `isDefinitiveCreateFailure`.
   */
  onCreateFailed?: (input: CreateEquipmentInput, definitive: boolean) => void;
  /**
   * Called for EVERY successful create, for the same reason as `onCreateFailed`. The
   * modal uses it to forget the submission (it holds the contact's details) at once.
   */
  onCreateSucceeded?: (input: CreateEquipmentInput, output: CreateEquipmentOutput) => void;
  /**
   * When given, the success message carries an Open button that calls this with the new
   * unit's document id (ISS-655). Not a redirect: the form has closed and the user may
   * already be doing something else.
   */
  openGenerator?: (id: string) => void;
}

/**
 * Placeholders whose create has settled but whose list has not been refreshed yet,
 * per QueryClient. Refreshing while ANOTHER create is still running would drop that
 * create's placeholder (the refetch replaces the list), so a settled create waits for
 * the last one and that one cleans up for everybody.
 */
const settledPlaceholders = new WeakMap<QueryClient, Set<string>>();

function removePlaceholders(queryClient: QueryClient, ids: ReadonlySet<string>): void {
  if (ids.size === 0) return;
  queryClient.setQueryData<Equipment[]>(dataHubEquipmentKeys.list(), (old) =>
    old?.filter((e) => !ids.has(e.equipmentId))
  );
  queryClient.setQueriesData<Generator[]>({ queryKey: GENERATOR_LISTS_KEY }, (old) =>
    old?.filter((g) => !ids.has(g.id))
  );
}

/**
 * A finished create's placeholder becomes the real unit AT ONCE, instead of sitting on
 * "Creating…" until the last overlapping create settles and the lists refetch. If the
 * real unit is already listed (a replay of an earlier create), the placeholder is
 * dropped instead, so the unit never shows twice.
 */
function replacePlaceholder(
  queryClient: QueryClient,
  pendingId: string,
  generator: { id: string; generatorId: string }
): void {
  queryClient.setQueryData<Equipment[]>(dataHubEquipmentKeys.list(), (old) => {
    if (!old) return old;
    if (old.some((e) => e.equipmentId === generator.id)) return old.filter((e) => e.equipmentId !== pendingId);
    return old.map((e) =>
      e.equipmentId === pendingId
        ? { ...e, equipmentId: generator.id, appSheetGeneratorId: generator.generatorId, generatorIdFromAppSheet: generator.generatorId }
        : e
    );
  });
  queryClient.setQueriesData<Generator[]>({ queryKey: GENERATOR_LISTS_KEY }, (old) => {
    if (!old) return old;
    if (old.some((g) => g.id === generator.id)) return old.filter((g) => g.id !== pendingId);
    return old.map((g) => (g.id === pendingId ? { ...g, id: generator.id, generatorId: generator.generatorId } : g));
  });
}

/** What `withNeedsSignal` calls this action ("Needs signal to create equipment. …"). */
export const CREATE_EQUIPMENT_NEEDS_SIGNAL_ACTION = 'create equipment';

/** Display-only stand-in for the Field Service generators list. */
function pendingGenerator(id: string, input: CreateGeneratorInput): Generator {
  return {
    id,
    generatorId: '',
    clientName: input.clientName,
    siteName: input.siteName || undefined,
    unitName: input.clientNameSubAccount || undefined,
    address: input.street || undefined,
    city: input.city || undefined,
    state: input.state || undefined,
    zip: input.zip || undefined,
    branch: input.branch || undefined,
    status: 'Active',
    make: input.generatorMake || undefined,
    model: input.generatorModel || undefined,
    kw: input.generatorKW ? Number(input.generatorKW) || undefined : undefined,
  };
}

/**
 * Codes the server throws BEFORE AppSheet is asked to create anything (validation,
 * access, a disabled write path). It never took the create lock, so a retry with the
 * same idempotency key is safe and will not be refused.
 *
 * Everything else is treated as AMBIGUOUS (functions/src/projects/generatorApi.ts): a
 * timeout, an AppSheet 5xx or a missing minted key all HOLD the lock for its 10-minute
 * lease because the row may exist, and `already-exists` means an earlier attempt still
 * holds it. An AppSheet rejection of the Add DOES release the lock, but it rethrows a
 * raw error that the access wrapper turns into a generic `internal` ("An unexpected
 * error occurred"), indistinguishable here from an ambiguous failure — so it gets the
 * cautious "check the list first" advice too. Telling that user "just retry" would be
 * wrong twice.
 */
const DEFINITIVE_CODES = new Set([
  'functions/invalid-argument',
  'functions/failed-precondition',
  'functions/permission-denied',
  'functions/unauthenticated',
  'functions/not-found',
]);

export function isDefinitiveCreateFailure(error: unknown): boolean {
  return error instanceof FirebaseError && DEFINITIVE_CODES.has(error.code);
}

/** Server `HttpsError` messages are human-written; a bare "INTERNAL" is not a message. */
function serverMessage(error: FirebaseError): string | null {
  const msg = error.message.trim().replace(/\.\s*$/, '');
  return msg && msg.toUpperCase() !== 'INTERNAL' ? msg : null;
}

/**
 * The ONE `functions/internal` message worth showing: createGenerator's own advice
 * after the unit was created in AppSheet but not saved locally ("… do NOT submit it
 * again"). Every other `internal` — including the `withAnyModuleAccess` wrapper's
 * generic "An unexpected error occurred", which is what an AppSheet rejection arrives
 * as — carries no guidance, so it gets the "may or may not" text instead.
 */
const KNOWN_INTERNAL_ADVICE = /do not submit/i;

/** Said on every outcome where the unit may exist without its follow-ups. */
const ADD_FOLLOW_UPS =
  ' If the unit appears, add the contact or QuickBooks customer you chose from its page.';

/**
 * The create-step error, worded for someone whose form has already closed.
 *
 * Server messages are passed through only for DEFINITIVE codes (validation text the
 * server wrote for the user) and for `KNOWN_INTERNAL_ADVICE`. `failed-precondition`
 * is reworded (it names internal switches) and every other `internal` is replaced.
 */
const DEFAULT_RESTORE_HINT = 'open New Generator to restore what you entered';

export function createErrorMessage(error: unknown, restoreHint: string = DEFAULT_RESTORE_HINT): string {
  const restore = ` ${restoreHint.charAt(0).toUpperCase()}${restoreHint.slice(1)}.`;
  const checkFirst =
    ' Check the generators list in a few minutes before entering it again — ' +
    `if it is not there, ${restoreHint}.` +
    ADD_FOLLOW_UPS;
  if (error instanceof FirebaseError) {
    const msg = serverMessage(error);
    if (error.code === 'functions/failed-precondition') {
      // The server's own wording here names internal switches ("writeback is
      // disabled", "no active AppSheet connection"). Say what it means instead.
      return `The unit was not created: OfficeHub cannot reach AppSheet to create units right now. Please tell an administrator.${restore}`;
    }
    if (isDefinitiveCreateFailure(error)) {
      return `The unit was not created${msg ? `: ${msg}` : ''}.${restore}`;
    }
    switch (error.code) {
      case 'functions/already-exists':
        return 'This unit is already being created from an earlier attempt and should appear in the list shortly. ' +
          `Please do not enter it again.${ADD_FOLLOW_UPS}`;
      case 'functions/deadline-exceeded':
        return `Creating this unit took longer than expected, and it may still have been created.${checkFirst}`;
      case 'functions/internal':
        // e.g. "The generator was created (ID …) but could not be saved locally … do NOT
        // submit it again." — the server's own words are the right advice here. Anything
        // else (the access wrapper's generic text) falls through to "may or may not".
        if (msg && KNOWN_INTERNAL_ADVICE.test(msg)) return `${msg}.${ADD_FOLLOW_UPS}`;
        break;
      default:
        break;
    }
  }
  return `Something went wrong, and the unit may or may not have been created.${checkFirst}`;
}

/** A replayed create does not re-send `qboLinks` (see runCreate); this says so. */
export const REPLAY_QBO_WARNING =
  'The unit already existed, so the QuickBooks link was not changed. Check it on the unit page.';

async function runCreate(
  input: CreateEquipmentInput,
  { onGeneratorCreated, onGeneratorIdMissing }: Pick<UseCreateEquipmentOptions, 'onGeneratorCreated' | 'onGeneratorIdMissing'>
): Promise<CreateEquipmentOutput> {
  // Waits past the server's 120s timeout (see CREATE_GENERATOR_CALLABLE_TIMEOUT_MS).
  const create = httpsCallable<CreateGeneratorInput, CreateGeneratorResult>(functions, 'createGenerator', {
    timeout: CREATE_GENERATOR_CALLABLE_TIMEOUT_MS,
  });
  const { data: generator } = await create(input.generator);
  const warnings: string[] = [];

  // A caller's bug must not turn a created unit into a failed job (and a restore
  // offer for a unit that exists), so neither callback can throw into the sequence.
  const notify = (fn: () => void, name: string) => {
    try {
      fn();
    } catch (error) {
      console.error(`[useCreateEquipment] ${name} threw`, error);
    }
  };

  if (!generator?.generatorId) {
    notify(() => onGeneratorIdMissing?.(input), 'onGeneratorIdMissing');
    warnings.push(
      'The unit was created, but its ID did not come back, so anything attached to it was skipped. ' +
        'Search for it before entering it again.'
    );
    return { generator, warnings };
  }
  notify(() => onGeneratorCreated?.(input, generator), 'onGeneratorCreated');

  // Both follow-ups need the doc id too. If the create reported success without it,
  // say so instead of skipping silently.
  if ((input.contact || input.qboLink) && !generator.id) {
    warnings.push(
      'The unit was created, but its ID did not come back, so the client details were not attached. Add them from the unit page.'
    );
    return { generator, warnings };
  }

  let contact: AttachGeneratorContactResult | undefined;
  if (input.contact && generator.generatorId) {
    try {
      const attach = httpsCallable<AttachGeneratorContactInput, AttachGeneratorContactResult>(
        functions,
        'fieldService_attachGeneratorContact',
        { timeout: ATTACH_CONTACT_TIMEOUT_MS }
      );
      ({ data: contact } = await attach({ generatorId: generator.generatorId, ...input.contact }));
    } catch (error) {
      warnings.push(
        `The unit was created, but the contact was not saved: ${contactMessageFor(error)} Add it from the unit page.`
      );
    }
  }

  // NOT on a replay. `replayed` means this key matched an earlier create (up to 24h old),
  // and `qboLinks` is a wholesale replace — billing may have stamped links onto that unit
  // since, and a one-element array would wipe them (CLAUDE.md defect shape #1).
  // But skipping must not be SILENT: the only way this UI replays is Restore after a
  // step-1 failure, and when that failure was ambiguous (a timeout after the unit
  // landed) the earlier attempt never reached this step — so the chosen customer may
  // never have been linked. Say so and send the user to the unit page.
  if (input.qboLink && generator.replayed) {
    warnings.push(REPLAY_QBO_WARNING);
  } else if (input.qboLink && generator.id) {
    try {
      const update = httpsCallable<UpdateGeneratorInput, unknown>(
        functions,
        'updateGenerator',
        { timeout: UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS }
      );
      await update({ generatorId: generator.id, updates: { qboLinks: [input.qboLink] } });
    } catch {
      warnings.push('The unit was created, but the QuickBooks link was not saved. Set it from the unit page.');
    }
  }

  return { generator, contact, warnings };
}

/** How long the "created" message stays up when it carries an Open button (the default is 2-3 s). */
const OPEN_BUTTON_TOAST_MS = 8000;

/**
 * @return A mutation whose `mutate` returns immediately; the job runs in the background.
 *
 * @example
 * const createEquipment = useCreateEquipment({ onCreateFailed: rememberForRestore });
 * createEquipment.mutate({ generator, contact, qboLink });
 * onClose(); // close NOW — the toast and the placeholder row carry the rest
 */
export function useCreateEquipment({
  onCreateFailed,
  onCreateSucceeded,
  onGeneratorCreated,
  onGeneratorIdMissing,
  openGenerator,
  restoreHint,
}: UseCreateEquipmentOptions = {}) {
  const queryClient = useQueryClient();

  // Offline, a mutation would otherwise PAUSE silently with its placeholder and spinner
  // up after the form had closed. `withNeedsSignal` refuses before `onMutate` (so no
  // placeholder, no loading toast, no request) with ONE "Needs signal to create
  // equipment. Nothing was saved." toast. The modal checks first and stays open; this
  // is the backstop, and it still hands the entries back through `onCreateFailed`.
  const options = withNeedsSignal<CreateEquipmentOutput, unknown, CreateEquipmentInput, CreateEquipmentContext>(
    CREATE_EQUIPMENT_NEEDS_SIGNAL_ACTION,
    {
      mutationKey: CREATE_EQUIPMENT_MUTATION_KEY,
      // Never auto-retry: the SERVER owns idempotency (it locks on the form's key and
      // replays), and a silent client retry into a held lock would replace the real
      // error with "already being created". Same reasoning as
      // useAttachGeneratorContact / useCreateServiceTicket.
      retry: false,
      mutationFn: (input) => runCreate(input, { onGeneratorCreated, onGeneratorIdMissing }),
      onMutate: async (input) => {
        const toastId = toast.loading('Creating equipment…');
        const pendingId = newPendingGeneratorId();

        await Promise.all([
          queryClient.cancelQueries({ queryKey: dataHubEquipmentKeys.list(), exact: true }),
          queryClient.cancelQueries({ queryKey: GENERATOR_LISTS_KEY }),
        ]);

        // Quoting's Equipment list. Only patch a list that is already cached: fabricating
        // one would make an unloaded page render a single-row table as if it were complete.
        queryClient.setQueryData<Equipment[]>(dataHubEquipmentKeys.list(), (old) =>
          old ? [buildOptimisticEquipment(input.generator, pendingId), ...old] : old
        );

        // Field Service Generators page. PREFIX match on purpose: the key carries orgId.
        for (const [key, data] of queryClient.getQueriesData<Generator[]>({ queryKey: GENERATOR_LISTS_KEY })) {
          if (!data) continue;
          queryClient.setQueryData<Generator[]>(key, [pendingGenerator(pendingId, input.generator), ...data]);
        }

        return { toastId, pendingId };
      },
      onSuccess: (output, input, context) => {
        const { id, generatorId, replayed } = output.generator ?? {};
        if (context?.pendingId && id && generatorId) replacePlaceholder(queryClient, context.pendingId, { id, generatorId });
        const label = generatorId || 'The unit';
        const message = replayed ?
          `${label} was already created — showing the existing record.` :
          `${label} created.`;
        toast.success(
          openGenerator && id ?
            (t) => createElement(GeneratorCreatedToast, {
              message,
              onOpen: () => {
                toast.dismiss(t.id);
                openGenerator(id);
              },
            }) :
            message,
          // Long enough to notice and click when there is a button; the plain message keeps the default.
          openGenerator && id ? { id: context?.toastId, duration: OPEN_BUTTON_TOAST_MS } : { id: context?.toastId }
        );
        for (const warning of output.warnings) {
          toast.error(warning, { duration: 15000 });
        }
        onCreateSucceeded?.(input, output);
      },
      onError: (error, input, context) => {
        // The failed unit's placeholder goes at once — it is not coming.
        if (context?.pendingId) removePlaceholders(queryClient, new Set([context.pendingId]));
        // Long-lived: the form is already closed, so this is the only record that the
        // unit was not created and where the entered details went.
        toast.error(createErrorMessage(error, restoreHint), { id: context?.toastId, duration: 15000 });
        onCreateFailed?.(input, isDefinitiveCreateFailure(error));
      },
      onSettled: (_output, error, input, context) => {
        // Refused offline before anything happened: nothing to refresh.
        if (isNeedsSignalError(error)) return;
        const settled = settledPlaceholders.get(queryClient) ?? new Set<string>();
        settledPlaceholders.set(queryClient, settled);
        if (context?.pendingId) settled.add(context.pendingId);

        // These never carry placeholders, so they refresh straight away.
        void queryClient.invalidateQueries({ queryKey: generatorKeys.all });
        if (input.contact) {
          // Contact attach results live on the GENERATOR doc's Contacts column — see the
          // onSettled comment in useAttachGeneratorContact for why these are load-bearing.
          void queryClient.invalidateQueries({ queryKey: sharedContactKeys.all });
          void queryClient.invalidateQueries({ queryKey: dataHubContactKeys.all });
        }

        // The lists DO carry placeholders. This mutation still counts as running while its
        // own onSettled executes, so > 1 means ANOTHER create is in flight: refreshing now
        // would drop its placeholder. The last create to settle refreshes for everyone.
        if (queryClient.isMutating({ mutationKey: CREATE_EQUIPMENT_MUTATION_KEY }) > 1) return;
        const done = new Set(settled);
        settled.clear();
        void Promise.allSettled([
          // On failure too: an ambiguous create may have landed.
          queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all }),
          queryClient.invalidateQueries({ queryKey: GENERATOR_LISTS_KEY }),
        ]).then(() => {
          // A successful refetch already replaced these rows with real ones. A FAILED
          // refetch leaves the old list — without this, "Creating…" would sit there forever.
          removePlaceholders(queryClient, done);
        });
      },
    }
  );

  return useMutation<CreateEquipmentOutput, unknown, CreateEquipmentInput, CreateEquipmentContext>({
    ...options,
    onError: (error, input, context, mutationContext) => {
      // withNeedsSignal toasts a refusal and skips the caller's onError; nothing was
      // sent, so the entries go back as a DEFINITIVE failure (a restore is safe).
      if (isNeedsSignalError(error)) onCreateFailed?.(input, true);
      return options.onError?.(error, input, context, mutationContext);
    },
  });
}

/**
 * Warn before the tab closes while a create is still running: the job lives in this
 * tab, and closing it abandons the create and its follow-ups with no record at all.
 * Mounted route-wide by `WarnWhileCreatingEquipment` in ModuleLayout, so it outlives
 * the form that started the create.
 */
export function useWarnWhileCreatingEquipment(): void {
  useWarnBeforeUnload(useIsMutating({ mutationKey: CREATE_EQUIPMENT_MUTATION_KEY }) > 0);
}
